import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import RecruiterLayout from "@/components/layout/RecruiterLayout";
import api, { formatApiError } from "@/lib/api";
import { GitCompareArrows, User } from "lucide-react";

export default function RecruiterCompare() {
  const [params] = useSearchParams();
  const ids = params.get("ids") || "";
  const [players, setPlayers] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!ids) return;
    api.get(`/recruiter/compare?ids=${ids}`).then(({ data }) => setPlayers(data.players)).catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "Compare failed"));
  }, [ids]);

  const metricKeys = Array.from(new Set(players.flatMap((p) => Object.keys(p.verified_metrics || {}))));

  const Row = ({ label, render }) => (
    <tr className="border-b border-slate-100">
      <td className="p-3 label-overline text-[10px] text-slate-500 whitespace-nowrap">{label}</td>
      {players.map((p) => <td key={p.id} data-testid={`compare-cell-${p.id}`} className="p-3 text-center font-data text-[#0A192F]">{render(p)}</td>)}
    </tr>
  );

  return (
    <RecruiterLayout>
      <div className="p-6 lg:p-8 max-w-6xl">
        <div className="label-overline text-[#E63946] flex items-center gap-2"><GitCompareArrows className="w-4 h-4" /> Side-by-Side</div>
        <h1 data-testid="compare-title" className="font-display text-4xl uppercase tracking-tighter mt-2">Player Comparison</h1>

        {err && <p className="mt-4 text-slate-600">{err}</p>}
        {!err && players.length < 2 && <p className="mt-4 text-slate-500">Select at least 2 players from the Search page to compare. <Link to="/recruiter" className="text-[#E63946] font-semibold">Go to Search →</Link></p>}

        {players.length >= 2 && (
          <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table data-testid="compare-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="p-3"></th>
                  {players.map((p) => (
                    <th key={p.id} className="p-3 text-center">
                      <Link to={`/recruiter/players/${p.id}`} className="inline-block">
                        <div className="w-14 h-14 rounded-sm overflow-hidden bg-slate-100 mx-auto">{p.profile_photo_url ? <img src={p.profile_photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><User className="w-6 h-6 text-slate-300" /></div>}</div>
                        <div className="font-display uppercase tracking-tight mt-2 hover:text-[#E63946]">{p.first_name} {p.last_name}</div>
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <Row label="CBR Rating" render={(p) => p.cbr_overall_rating != null ? Math.round(p.cbr_overall_rating * 100) / 100 : "—"} />
                <Row label="Grad Year" render={(p) => p.graduation_year || "—"} />
                <Row label="Province" render={(p) => p.province || "—"} />
                <Row label="Primary Pos" render={(p) => p.primary_position || "—"} />
                <Row label="Secondary Pos" render={(p) => p.secondary_position || (p.secondary_positions || []).join(", ") || "—"} />
                <Row label="Height (cm)" render={(p) => p.height_cm || "—"} />
                <Row label="Weight (kg)" render={(p) => p.weight_kg || "—"} />
                <Row label="Bats / Throws" render={(p) => `${p.bats || "-"}/${p.throws || "-"}`} />
                <Row label="GPA" render={(p) => p.gpa || "—"} />
                <Row label="National Rank" render={(p) => p.rankings?.national ? `#${p.rankings.national}` : "—"} />
                <Row label="Provincial Rank" render={(p) => p.rankings?.provincial ? `#${p.rankings.provincial}` : "—"} />
                <Row label="Position Rank" render={(p) => p.rankings?.position ? `#${p.rankings.position}` : "—"} />
                {metricKeys.map((k) => <Row key={k} label={k.replace(/_/g, " ")} render={(p) => p.verified_metrics?.[k]?.value ?? "—"} />)}
                <Row label="Commitment" render={(p) => p.committed_to || p.commitment_status || "—"} />
              </tbody>
            </table>
          </div>
        )}
      </div>
    </RecruiterLayout>
  );
}
